import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Stack,
  TextField,
  Typography,
  FormLabel,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ToggleButton,
  Tooltip,
  Button,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { ArrowDropDownIcon } from "@mui/x-date-pickers/icons";
import InfoIcon from "@mui/icons-material/Info";
import "../../../components/styles/popupStyle.css";
import { GolfPropertyTime } from "../../../constants/PropertyInformation";
import {
  DAYS,
  WEEKDAY_LABELS_MON_SUN,
  dateRangeCoversSelectedWeekdays,
  formatDaysOfWeekCommaSeparated,
  parseDaysOfWeekStored,
} from "../../../constants/Days";
import AlertComponent from "../../subcomponents/AlertComponent";
import { textStyle } from "../../styles/Style";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  checkData,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import { removeSampleTestArray } from "../../actions/cleanup";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  buildRetailItemNumberOptions,
  linkedRetailItemOptionValue,
  parseRetailItemNumberFromOption,
} from "../../../constants/DefaultRetailItems";
import {
  GOLF_RATE_PLAN_FIELD_AVAILABLE_DAYS,
  GOLF_RATE_PLAN_FIELD_CART_FEE,
  GOLF_RATE_PLAN_FIELD_CART_FEE_RETAIL_ITEM,
  GOLF_RATE_PLAN_FIELD_COURSES,
  GOLF_RATE_PLAN_FIELD_END_DATE,
  GOLF_RATE_PLAN_FIELD_END_TIME,
  GOLF_RATE_PLAN_FIELD_ENTRY_FEE,
  GOLF_RATE_PLAN_FIELD_ENTRY_FEE_RETAIL_ITEM,
  GOLF_RATE_PLAN_FIELD_GREEN_FEE,
  GOLF_RATE_PLAN_FIELD_GREEN_FEE_RETAIL_ITEM,
  GOLF_RATE_PLAN_FIELD_RATE_TYPE,
  GOLF_RATE_PLAN_FIELD_START_DATE,
  GOLF_RATE_PLAN_FIELD_START_TIME,
  golfRatePlanValidationMessages,
} from "../../../constants/GolfRatePlanFields";

type Day = (typeof DAYS)[number];

const emptyWeekState = (): Record<Day, boolean> => ({
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false,
});

const formatDateYmd = (value: Dayjs | null): string | null =>
  value?.isValid() ? value.format("YYYY-MM-DD") : null;

const formatTimeHm = (value: Dayjs | null | undefined): string | null =>
  value && dayjs(value).isValid() ? dayjs(value).format("HH:mm") : null;

const parseStoredTime = (
  raw: unknown,
  fallback: Dayjs,
): Dayjs => {
  if (raw == null || raw === "") {
    return fallback;
  }
  if (dayjs.isDayjs(raw)) {
    return raw;
  }
  if (typeof raw === "string" && /^\d{1,2}:\d{2}/.test(raw.trim())) {
    return dayjs(`${dayjs().format("YYYY-MM-DD")}T${raw.trim()}`);
  }
  const parsed = dayjs(raw as string);
  return parsed.isValid() ? parsed : fallback;
};

const weekStateFromStored = (raw: unknown): Record<Day, boolean> => {
  const next = emptyWeekState();
  const labels =
    typeof raw === "string"
      ? parseDaysOfWeekStored(raw)
      : Array.isArray(raw)
        ? parseDaysOfWeekStored(raw.join(", "))
        : raw && typeof raw === "object"
          ? WEEKDAY_LABELS_MON_SUN.filter(
              (label) =>
                Boolean((raw as Record<string, boolean>)[label.toLowerCase()]),
            )
          : [];
  for (const label of labels) {
    next[label.toLowerCase() as Day] = true;
  }
  return next;
};

const selectedWeekdayLabels = (weekState: Record<Day, boolean>): string[] =>
  WEEKDAY_LABELS_MON_SUN.filter(
    (label) => weekState[label.toLowerCase() as Day],
  );

const GolfRatePlan = ({
  data,
  fieldValues,
  golfRetailItems,
}: {
  data: any;
  fieldValues: any;
  golfRetailItems?: unknown;
}) => {
  const [courses, setCourses] = useState<string | null>(null);
  const [rateType, setRateType] = useState<string | null>(null);
  const [greenFee, setGreenFee] = useState<string | null>(null);
  const [cartFee, setCartFee] = useState<string | null>(null);
  const [entryFee, setEntryFee] = useState<string | null>(null);
  const [greenFeeRetailItem, setGreenFeeRetailItem] = useState<string | null>(
    null,
  );
  const [cartFeeRetailItem, setCartFeeRetailItem] = useState<string | null>(
    null,
  );
  const [entryFeeRetailItem, setEntryFeeRetailItem] = useState<string | null>(
    null,
  );
  const [startTime, setStartTime] = useState<Dayjs | null>(
    GolfPropertyTime.StartTime,
  );
  const [endTime, setEndTime] = useState<Dayjs | null>(GolfPropertyTime.EndTime);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ratePlans, setRatePlans] = useState<any>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [courseList, setCourseList] = useState<string[]>([]);
  const [rateTypeList, setRateTypeList] = useState<string[]>([]);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [coursesError, setCoursesError] = useState<boolean>(false);
  const [rateTypeError, setRateTypeError] = useState<boolean>(false);
  const [startDateError, setStartDateError] = useState<boolean>(false);
  const [endDateError, setEndDateError] = useState<boolean>(false);
  const [availableDaysError, setAvailableDaysError] = useState<boolean>(false);
  const [greenFeeError, setGreenFeeError] = useState<boolean>(false);
  const [greenFeeRetailItemError, setGreenFeeRetailItemError] =
    useState<boolean>(false);
  const [weekState, setWeekState] =
    useState<Record<Day, boolean>>(emptyWeekState);

  const retailItemOptions = buildRetailItemNumberOptions(
    golfRetailItems ?? [],
  );

  const toggleDay = (day: Day) => {
    setWeekState((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
    setAvailableDaysError(false);
  };

  const isDateRangeInvalid = () =>
    Boolean(
      startDate &&
        endDate &&
        dayjs(endDate).isBefore(dayjs(startDate), "day"),
    );

  const showAlert = (message: string, type: "success" | "error") => {
    setAlertMessage(message);
    setAlertType(type);
    setOpenAlert(true);
  };

  const showDateRangeError = () => {
    showAlert(
      golfRatePlanValidationMessages.endDateBeforeStart,
      "error",
    );
  };

  const buildPayload = () => {
    const days = selectedWeekdayLabels(weekState);
    return {
      [GOLF_RATE_PLAN_FIELD_COURSES]: courses,
      [GOLF_RATE_PLAN_FIELD_RATE_TYPE]: rateType,
      [GOLF_RATE_PLAN_FIELD_START_DATE]: formatDateYmd(startDate),
      [GOLF_RATE_PLAN_FIELD_END_DATE]: formatDateYmd(endDate),
      [GOLF_RATE_PLAN_FIELD_START_TIME]: formatTimeHm(startTime),
      [GOLF_RATE_PLAN_FIELD_END_TIME]: formatTimeHm(endTime),
      [GOLF_RATE_PLAN_FIELD_AVAILABLE_DAYS]:
        formatDaysOfWeekCommaSeparated(days),
      [GOLF_RATE_PLAN_FIELD_GREEN_FEE]:
        greenFee === null || greenFee === "" ? null : Number(greenFee),
      [GOLF_RATE_PLAN_FIELD_CART_FEE]:
        cartFee === null || cartFee === "" ? null : Number(cartFee),
      [GOLF_RATE_PLAN_FIELD_ENTRY_FEE]:
        entryFee === null || entryFee === "" ? null : Number(entryFee),
      [GOLF_RATE_PLAN_FIELD_GREEN_FEE_RETAIL_ITEM]: greenFeeRetailItem,
      [GOLF_RATE_PLAN_FIELD_CART_FEE_RETAIL_ITEM]: cartFeeRetailItem,
      [GOLF_RATE_PLAN_FIELD_ENTRY_FEE_RETAIL_ITEM]: entryFeeRetailItem,
    };
  };

  const validateForm = (): boolean => {
    const days = selectedWeekdayLabels(weekState);
    const missingCourses = !courses;
    const missingRateType = !rateType;
    const missingStart = !startDate;
    const missingEnd = !endDate;
    const missingDays = days.length === 0;
    const missingGreenFee = greenFee === null || String(greenFee).trim() === "";
    const missingGreenFeeRetailItem =
      greenFeeRetailItem === null ||
      String(greenFeeRetailItem).trim() === "";

    setCoursesError(missingCourses);
    setRateTypeError(missingRateType);
    setStartDateError(missingStart);
    setEndDateError(missingEnd);
    setAvailableDaysError(missingDays);
    setGreenFeeError(missingGreenFee);
    setGreenFeeRetailItemError(missingGreenFeeRetailItem);

    if (
      missingCourses ||
      missingRateType ||
      missingStart ||
      missingEnd ||
      missingDays ||
      missingGreenFee ||
      missingGreenFeeRetailItem
    ) {
      showAlert(golfRatePlanValidationMessages.mandatory, "error");
      return false;
    }

    if (isDateRangeInvalid()) {
      showDateRangeError();
      setEndDateError(true);
      return false;
    }

    const startYmd = formatDateYmd(startDate);
    const endYmd = formatDateYmd(endDate);
    if (!dateRangeCoversSelectedWeekdays(startYmd, endYmd, days)) {
      setAvailableDaysError(true);
      showAlert(
        golfRatePlanValidationMessages.availableDaysNotCovered,
        "error",
      );
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setSelectedId(null);
    setEditClicked(false);
    setCourses(null);
    setRateType(null);
    setGreenFee(null);
    setCartFee(null);
    setEntryFee(null);
    setGreenFeeRetailItem(null);
    setCartFeeRetailItem(null);
    setEntryFeeRetailItem(null);
    setStartDate(dayjs());
    setEndDate(null);
    setStartTime(GolfPropertyTime.StartTime);
    setEndTime(GolfPropertyTime.EndTime);
    setWeekState(emptyWeekState());
    setCoursesError(false);
    setRateTypeError(false);
    setStartDateError(false);
    setEndDateError(false);
    setAvailableDaysError(false);
    setGreenFeeError(false);
    setGreenFeeRetailItemError(false);
  };

  const editRatePlan = (list: any) => {
    if (list == null) {
      resetForm();
      return;
    }

    list?.id && setSelectedId(list.id);
    setEditClicked(true);
    setCourses(list?.[GOLF_RATE_PLAN_FIELD_COURSES] ?? null);
    setRateType(list?.[GOLF_RATE_PLAN_FIELD_RATE_TYPE] ?? null);
    setGreenFee(
      list?.[GOLF_RATE_PLAN_FIELD_GREEN_FEE] != null
        ? String(list[GOLF_RATE_PLAN_FIELD_GREEN_FEE])
        : null,
    );
    setCartFee(
      list?.[GOLF_RATE_PLAN_FIELD_CART_FEE] != null
        ? String(list[GOLF_RATE_PLAN_FIELD_CART_FEE])
        : null,
    );
    setEntryFee(
      list?.[GOLF_RATE_PLAN_FIELD_ENTRY_FEE] != null
        ? String(list[GOLF_RATE_PLAN_FIELD_ENTRY_FEE])
        : null,
    );
    setGreenFeeRetailItem(
      list?.[GOLF_RATE_PLAN_FIELD_GREEN_FEE_RETAIL_ITEM] != null
        ? String(list[GOLF_RATE_PLAN_FIELD_GREEN_FEE_RETAIL_ITEM])
        : null,
    );
    setCartFeeRetailItem(
      list?.[GOLF_RATE_PLAN_FIELD_CART_FEE_RETAIL_ITEM] != null
        ? String(list[GOLF_RATE_PLAN_FIELD_CART_FEE_RETAIL_ITEM])
        : null,
    );
    setEntryFeeRetailItem(
      list?.[GOLF_RATE_PLAN_FIELD_ENTRY_FEE_RETAIL_ITEM] != null
        ? String(list[GOLF_RATE_PLAN_FIELD_ENTRY_FEE_RETAIL_ITEM])
        : null,
    );
    setStartDate(
      list?.[GOLF_RATE_PLAN_FIELD_START_DATE]
        ? dayjs(list[GOLF_RATE_PLAN_FIELD_START_DATE])
        : dayjs(),
    );
    setEndDate(
      list?.[GOLF_RATE_PLAN_FIELD_END_DATE]
        ? dayjs(list[GOLF_RATE_PLAN_FIELD_END_DATE])
        : null,
    );
    setStartTime(
      parseStoredTime(
        list?.[GOLF_RATE_PLAN_FIELD_START_TIME],
        GolfPropertyTime.StartTime,
      ),
    );
    setEndTime(
      parseStoredTime(
        list?.[GOLF_RATE_PLAN_FIELD_END_TIME],
        GolfPropertyTime.EndTime,
      ),
    );
    setWeekState(
      weekStateFromStored(list?.[GOLF_RATE_PLAN_FIELD_AVAILABLE_DAYS]),
    );
  };

  const updateRatePlan = () => {
    if (!validateForm()) {
      return;
    }
    const payload = buildPayload();
    setRatePlans((prev: any) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRatePlan(null);
    showAlert(`${GolfPropertyEnum.ratePlan} updated successfully`, "success");
  };

  const addRatePlan = () => {
    if (!validateForm()) {
      return;
    }
    const payload = buildPayload();
    setRatePlans((prev: any) =>
      updateData(
        prev != null
          ? [
              ...prev,
              {
                id: (ratePlans?.length ?? 0) + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      ),
    );
    setLastUpdatedRecord(payload);
    editRatePlan(null);
  };

  const deleteRatePlan = (id: number) => {
    setRatePlans(deleteItem(ratePlans ?? [], id));
  };

  useEffect(() => {
    if (!fieldValues) {
      return;
    }
    setRatePlans(fieldValues[GolfPropertyEnum.ratePlan]);
    const courseNameData = fieldValues[GolfPropertyEnum.courses];
    if (courseNameData && Array.isArray(courseNameData)) {
      setCourseList(
        fillAutocompleteValues(courseNameData, DNDFieldsEnum.COURSENAME, ""),
      );
    } else {
      setCourseList([]);
    }
    const rateTypeData = fieldValues[GolfPropertyEnum.rateTypes];
    if (rateTypeData && Array.isArray(rateTypeData)) {
      setRateTypeList(
        fillAutocompleteValues(rateTypeData, DNDFieldsEnum.RATETYPE, ""),
      );
    } else {
      setRateTypeList([]);
    }
  }, []);

  useEffect(() => {
    data(ratePlans);
    editClicked &&
      setSelectedId(checkData(ratePlans, selectedId) ? selectedId : null);
  }, [ratePlans]);

  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={textStyle}>
            Rate Plan
            <Tooltip
              title={tooltipComponent(golfTooltipHeaders.ratePlanHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px" justifyContent="space-between">
            {editClicked && (
              <ClearButton
                click={() => {
                  editRatePlan(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton
                click={() => {
                  editRatePlan(lastUpdatedRecord);
                }}
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" width="100%" gap="5px">
          <Autocomplete
            id="golfRatePlanCourses"
            disablePortal
            options={removeSampleTestArray(courseList)}
            sx={{ width: "100%" }}
            value={courses?.toString() ?? ""}
            disabled={!courseList.length}
            onChange={(_, v) => {
              setCourses(v);
              setCoursesError(false);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Courses" error={coursesError} />
            )}
          />
          <Tooltip title={tooltipComponent(golfTooltipHeaders.courseName)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Stack>

        <Stack direction="row" width="100%" gap="5px">
          <Autocomplete
            id="golfRatePlanRateType"
            disablePortal
            options={removeSampleTestArray(rateTypeList)}
            sx={{ width: "100%" }}
            value={rateType?.toString() ?? ""}
            disabled={!rateTypeList.length}
            onChange={(_, v) => {
              setRateType(v);
              setRateTypeError(false);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Rate Type" error={rateTypeError} />
            )}
          />
          <Tooltip title={tooltipComponent(golfTooltipHeaders.rateType)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Stack>

        <Stack
          className="form-two-col-row"
          direction="row"
          gap={1}
          width="100%"
        >
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              Start Date
              <Tooltip
                title={tooltipComponent(
                  golfTooltipHeaders.startDateRatesSetup,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={startDate}
                format="YYYY-MM-DD"
                sx={{ width: "100%" }}
                onChange={(value) => {
                  setStartDate(value);
                  setStartDateError(false);
                  if (
                    value &&
                    endDate &&
                    dayjs(endDate).isBefore(dayjs(value), "day")
                  ) {
                    setEndDate(null);
                    showDateRangeError();
                  }
                }}
                slotProps={{
                  textField: { error: startDateError },
                }}
              />
            </LocalizationProvider>
          </Stack>
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              End Date
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.endDateRatesSetup)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={endDate}
                format="YYYY-MM-DD"
                minDate={startDate ?? undefined}
                sx={{ width: "100%" }}
                onChange={(value: Dayjs | null) => {
                  if (!value) {
                    setEndDate(null);
                    return;
                  }
                  if (
                    startDate &&
                    dayjs(value).isBefore(dayjs(startDate), "day")
                  ) {
                    showDateRangeError();
                    setEndDate(null);
                    setEndDateError(true);
                    return;
                  }
                  setEndDate(value);
                  setEndDateError(false);
                }}
                slotProps={{
                  textField: { error: endDateError },
                }}
              />
            </LocalizationProvider>
          </Stack>
        </Stack>

        <Stack
          className="form-two-col-row"
          direction="row"
          gap={1}
          width="100%"
        >
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              Start Time
              <Tooltip
                title={tooltipComponent(
                  golfTooltipHeaders.startTimeRatesSetup,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                sx={{ width: "100%" }}
                value={startTime}
                onChange={(newValue) => setStartTime(newValue)}
              />
            </LocalizationProvider>
          </Stack>
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              End Time
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.endTimeRatesSetup)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                value={endTime}
                sx={{ width: "100%" }}
                onChange={(newValue) => setEndTime(newValue)}
              />
            </LocalizationProvider>
          </Stack>
        </Stack>

        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ArrowDropDownIcon />}
            aria-controls="golf-rate-plan-days-content"
            id="golf-rate-plan-days-header"
          >
            <Typography component="span">
              Available Days of Week
              <Tooltip
                title={tooltipComponent(
                  golfTooltipHeaders.availableDaysInAWeek,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack className="form-two-col-row" direction="row" spacing={1}>
              {DAYS.map((day) => (
                <ToggleButton
                  key={day}
                  value={day}
                  selected={weekState[day]}
                  color={availableDaysError ? "error" : "primary"}
                  onChange={() => toggleDay(day)}
                  className="week-toggle"
                >
                  {day.charAt(0).toUpperCase() + day.slice(1)}
                </ToggleButton>
              ))}
            </Stack>
            {availableDaysError &&
              selectedWeekdayLabels(weekState).length === 0 && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {golfRatePlanValidationMessages.atLeastOneDay}
                </Typography>
              )}
          </AccordionDetails>
        </Accordion>

        <Stack
          className="form-two-col-row"
          direction="row"
          gap={1}
          width="100%"
        >
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              Green Fee *
              <Tooltip title={tooltipComponent(golfTooltipHeaders.greenFee)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="greenFee"
              variant="outlined"
              placeholder="Green Fee"
              type="number"
              value={greenFee ?? ""}
              error={greenFeeError}
              onChange={(e) => {
                setGreenFee(e.target.value);
                setGreenFeeError(false);
              }}
            />
          </Stack>
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              Green Fee Retail Item *
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.greenFeeItem)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Autocomplete
              size="small"
              options={retailItemOptions}
              value={linkedRetailItemOptionValue(
                greenFeeRetailItem,
                retailItemOptions,
              )}
              disabled={!retailItemOptions.length}
              onChange={(_, v) => {
                setGreenFeeRetailItem(parseRetailItemNumberFromOption(v));
                setGreenFeeRetailItemError(false);
              }}
              isOptionEqualToValue={(a, b) => a === b}
              noOptionsText="No retail items available"
              openOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  error={greenFeeRetailItemError}
                  placeholder={
                    retailItemOptions.length
                      ? "Select retail item"
                      : "Add retail items first"
                  }
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack
          className="form-two-col-row"
          direction="row"
          gap={1}
          width="100%"
        >
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              Cart Fee
              <Tooltip title={tooltipComponent(golfTooltipHeaders.cartFee)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="cartFee"
              variant="outlined"
              placeholder="Cart Fee"
              type="number"
              value={cartFee ?? ""}
              onChange={(e) => setCartFee(e.target.value)}
            />
          </Stack>
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              Cart Fee Retail Item
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.cartFeeItem)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Autocomplete
              size="small"
              options={retailItemOptions}
              value={linkedRetailItemOptionValue(
                cartFeeRetailItem,
                retailItemOptions,
              )}
              disabled={!retailItemOptions.length}
              onChange={(_, v) =>
                setCartFeeRetailItem(parseRetailItemNumberFromOption(v))
              }
              isOptionEqualToValue={(a, b) => a === b}
              noOptionsText="No retail items available"
              openOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={
                    retailItemOptions.length
                      ? "Select retail item"
                      : "Add retail items first"
                  }
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack
          className="form-two-col-row"
          direction="row"
          gap={1}
          width="100%"
        >
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              Entry Fee
              <Tooltip title={tooltipComponent(golfTooltipHeaders.entryFee)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="entryFee"
              variant="outlined"
              placeholder="Entry Fee"
              type="number"
              value={entryFee ?? ""}
              onChange={(e) => setEntryFee(e.target.value)}
            />
          </Stack>
          <Stack direction="column" gap="2px" sx={{ width: "100%" }}>
            <FormLabel component="legend">
              Entry Fee Retail Item
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.entryFeeItem)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Autocomplete
              size="small"
              options={retailItemOptions}
              value={linkedRetailItemOptionValue(
                entryFeeRetailItem,
                retailItemOptions,
              )}
              disabled={!retailItemOptions.length}
              onChange={(_, v) =>
                setEntryFeeRetailItem(parseRetailItemNumberFromOption(v))
              }
              isOptionEqualToValue={(a, b) => a === b}
              noOptionsText="No retail items available"
              openOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={
                    retailItemOptions.length
                      ? "Select retail item"
                      : "Add retail items first"
                  }
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction="row" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateRatePlan() : addRatePlan())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
            Rate Plan
          </Button>
        </Stack>

        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Rate Plans Available</Typography>
          <Stack direction="column" gap="10px">
            {ratePlans ? (
              <DraggableItemList
                itemData={ratePlans}
                onOrderUpdate={(order) => setRatePlans(order)}
                onEdit={(info) => editRatePlan(info)}
                onDelete={(id) => deleteRatePlan(id)}
                code={DNDFieldsEnum.COURSES}
                name={DNDFieldsEnum.RATETYPE_CAMEL}
              />
            ) : (
              <Typography variant="body2">
                No Rate Plan Information Available
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default GolfRatePlan;
